import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

// Catalog-level Stripe authorization/tenant-review (Aug 2026).
//
// Shows the tenant everything needed to decide on an `authorized`
// (not yet captured) order: items/quantities, subtotal, discount, tax,
// tip, delivery fee, the authorized maximum, the proposed final
// capture amount, and how much would be released. Supports reducing
// or removing items (never increasing quantity, never a free-form
// price) and either "Accept & Capture" or "Reject & Release
// Authorization" — both explicit, both disabled while a request is in
// flight, and both surface a `reconciliation_required` outcome as a
// dead end (no retry suggestion) rather than inviting a second attempt
// against a payment whose true state is not yet known.
//
// Tenant-adjustment/partial-capture/Uber-parity correction (Aug 2026,
// UBE-ORD-000030) — confirmed staging defect: this modal used to fetch
// the review view ONCE on mount and never recomputed the on-screen
// subtotal/total/proposed-capture/release figures when the tenant
// changed a quantity — only the per-row quantity number itself updated
// locally. Every quantity change now triggers a fresh, server-
// authoritative preview call (debounced, request-sequenced so a
// slower response for an earlier quantity can never overwrite a newer
// one), and Accept sends back the exact `snapshot_hash` of the preview
// currently on screen — the backend refuses to proceed if that no
// longer matches a fresh recompute (`status: "stale_preview"`).
function formatMoney(cents, currency = "USD") {
  const n = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

const PREVIEW_DEBOUNCE_MS = 300;

export default function TenantReviewModal({ orderId, onClose, onResolved }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [originalItems, setOriginalItems] = useState([]);
  const [preview, setPreview] = useState(null);
  // Set the INSTANT a quantity changes (before the debounce timer even
  // starts) and cleared only once a preview response matching that
  // exact change has landed — a strictly wider window than
  // `previewPending` (which only covers the network call itself), so
  // Accept is disabled through the debounce gap too, not just while a
  // request is literally in flight.
  const [previewStale, setPreviewStale] = useState(false);
  const [previewPending, setPreviewPending] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [quantities, setQuantities] = useState({});
  // Catalog Fulfillment PR 2 (Aug 2026) — the tenant's fulfillment
  // decision for THIS order, shown only when the server-computed
  // `allowed_fulfillment_choices` offers more than one option (an
  // Uber-quoted order the tenant may choose to fulfill themselves
  // instead). `null` until a preview response sets it; always reset to
  // the server's own default (the list's first entry) whenever the
  // allowed list changes, never carried over stale across a refresh.
  const [fulfillmentChoice, setFulfillmentChoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Out-of-order-response protection — only the response matching the
  // MOST RECENTLY issued request is ever applied; a slower response
  // for an earlier (now-superseded) quantity is silently dropped.
  const requestSeqRef = useRef(0);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const currency = (preview?.currency || "USD").toUpperCase();

  const buildAdjustmentsFrom = useCallback((qtyMap) => {
    const adjustments = {};
    let changed = false;
    originalItems.forEach((it) => {
      const id = it.item_id || it.id;
      const newQty = qtyMap[id] ?? it.quantity;
      if (newQty !== it.quantity) {
        adjustments[id] = newQty;
        changed = true;
      }
    });
    return changed ? adjustments : undefined;
  }, [originalItems]);

  const runPreview = useCallback(async (adjustmentsForThisCall, { isInitial = false } = {}) => {
    const seq = ++requestSeqRef.current;
    if (isInitial) { setLoading(true); setError(null); } else { setPreviewPending(true); }
    setPreviewFailed(false);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/tenant-review/preview`, {
        adjustments: adjustmentsForThisCall,
      });
      if (!mountedRef.current || seq !== requestSeqRef.current) return; // stale/out-of-order/unmounted
      if (isInitial) {
        setOriginalItems(data.items || []);
        const initialQty = {};
        (data.items || []).forEach((it) => { initialQty[it.item_id || it.id] = it.quantity; });
        setQuantities(initialQty);
      } else {
        setPreviewStale(false);
      }
      setPreview(data);
      const allowedChoices = data.allowed_fulfillment_choices || [];
      setFulfillmentChoice((prev) => (prev && allowedChoices.includes(prev) ? prev : (allowedChoices[0] || null)));
    } catch (e) {
      if (!mountedRef.current || seq !== requestSeqRef.current) return;
      if (isInitial) {
        const det = e?.response?.data?.detail;
        setError((det && typeof det === "object" && det.message) || "Could not load this order for review.");
      } else {
        setPreviewFailed(true);
      }
    } finally {
      if (mountedRef.current && seq === requestSeqRef.current) {
        if (isInitial) setLoading(false); else setPreviewPending(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    runPreview(undefined, { isInitial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const scheduleQuantityChange = (nextQuantities) => {
    setQuantities(nextQuantities);
    setPreviewStale(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runPreview(buildAdjustmentsFrom(nextQuantities));
    }, PREVIEW_DEBOUNCE_MS);
  };

  const decrement = (itemId, originalQty) => {
    scheduleQuantityChange({ ...quantities, [itemId]: Math.max(0, (quantities[itemId] ?? originalQty) - 1) });
  };
  const increment = (itemId, originalQty) => {
    // Never allow increasing above the ORIGINAL quantity — this action
    // can only reduce or remove items.
    scheduleQuantityChange({ ...quantities, [itemId]: Math.min(originalQty, (quantities[itemId] ?? originalQty) + 1) });
  };

  const hasAdjustments = buildAdjustmentsFrom(quantities) !== undefined;
  const isEmptyOrder = preview?.is_empty_order === true;
  // Catalog Fulfillment PR 4 (Aug 2026) — `allowed_fulfillment_choices`
  // (server-computed by `compute_allowed_fulfillment_choices`) already
  // fully encodes this order's checkout-time fulfillment snapshot: a
  // single-entry `["pickup"]` is a Customer Pickup order, `["own"]` is
  // an Own-Delivery-only order, and `["uber"]`/`["uber","own"]` are
  // Uber orders (the latter only when the catalog's override flag was
  // snapshotted on at checkout). No separate field is needed to label
  // the Accept action or to detect an actual Uber→Own switch.
  const allowedFulfillmentChoices = preview?.allowed_fulfillment_choices || [];
  const isPickupOrder = allowedFulfillmentChoices.length === 1 && allowedFulfillmentChoices[0] === "pickup";
  const isUberChoice = fulfillmentChoice === "uber";
  const isOwnDeliveryChoice = fulfillmentChoice === "own";
  // True only when this order was actually quoted through Uber (i.e.
  // Uber is one of the legal choices) and the tenant has selected
  // "own" instead — never true for a catalog whose checkout-time
  // snapshot was Own Delivery to begin with (no Uber quote ever
  // existed to switch away from).
  const isUberToOwnSwitch = isOwnDeliveryChoice && allowedFulfillmentChoices.includes("uber");
  // Recalculation/quote-refresh pending, a failed refresh, or no
  // preview at all yet — Accept (and the empty-order reject action)
  // must never be reachable until the on-screen numbers are the
  // server's own latest, successfully-computed truth.
  const previewNotReady = loading || previewStale || previewPending || previewFailed || !preview;

  const retryPreview = () => runPreview(buildAdjustmentsFrom(quantities));

  const accept = async () => {
    if (processing || previewNotReady || isEmptyOrder) return;
    const adjustments = buildAdjustmentsFrom(quantities);
    // Catalog Fulfillment PR 4 (Aug 2026) — a switch from the
    // customer-authorized Uber quote to Own Delivery gets its own,
    // more specific confirmation: it must be unmistakable that the
    // customer's already-authorized Uber charge is being KEPT exactly
    // as-is (never re-priced, never re-captured) and that no Uber
    // dispatch will be created — distinct from the ordinary "accept
    // and capture" confirmation every other order gets.
    const confirmMessage = isUberToOwnSwitch
      ? "Switch this order from Uber to Own Delivery? The customer keeps the exact delivery charge they already authorized through Uber — this will NOT create an Uber dispatch, and the charged amount will not change."
      : isPickupOrder
      ? (adjustments
          ? "Accept this order with the adjustments shown for customer pickup and capture the final amount from the customer's card? (Delivery charge is $0.00 — other charges are captured normally.)"
          : "Accept this order for customer pickup and capture the full authorized amount from the customer's card? (Delivery charge is $0.00 — other charges are captured normally.)")
      : isUberChoice
      ? (adjustments
          ? "Accept this order with the adjustments shown, request Uber delivery, and capture the final amount from the customer's card?"
          : "Accept this order, request Uber delivery, and capture the full authorized amount from the customer's card?")
      : isOwnDeliveryChoice
      ? (adjustments
          ? "Accept this order with the adjustments shown, fulfill it via Own Delivery, and capture the final amount from the customer's card?"
          : "Accept this order, fulfill it via Own Delivery, and capture the full authorized amount from the customer's card?")
      : adjustments
      ? "Accept this order with the adjustments shown and capture the final amount from the customer's card?"
      : "Accept this order and capture the full authorized amount from the customer's card?";
    if (!window.confirm(confirmMessage)) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/tenant-review/accept`, {
        adjustments, approved_snapshot_hash: preview.snapshot_hash,
        approved_quote_id: preview.local_delivery_quote_id,
        fulfillment_choice: fulfillmentChoice || undefined,
      });
      if (data.status === "stale_preview") {
        toast.error("This order's totals changed since you last reviewed them. Refreshing — please review and try again.");
        runPreview(adjustments);
        return;
      }
      if (data.status === "quote_refresh_required") {
        // Quote-finalized adjustment correction (Aug 2026, UBE-ORD-000030
        // PO follow-up, Defect A) — the delivery quote finalized in the
        // on-screen preview is no longer usable (expired, superseded by
        // a competing update, or the order changed underneath it).
        // Never silently substitutes a different quote/amount — always
        // forces a fresh preview (a genuinely new Uber quote) that the
        // tenant must explicitly re-confirm before anything is charged.
        toast.error("This delivery quote is no longer valid. Refreshing with a new quote — please review and try again.");
        runPreview(adjustments);
        return;
      }
      if (data.status === "exceeds_authorization") {
        toast.error("The final total exceeds the amount authorized on the customer's card. Reduce the order further or reject it.");
        return;
      }
      if (data.status === "reconciliation_required") {
        setOutcome({ kind: "reconciliation_required", message: data.message });
        return;
      }
      if (data.status === "capture_failed") {
        toast.error(data.message || "The payment could not be captured.");
        return;
      }
      toast.success("Order accepted and payment captured.");
      onResolved && onResolved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not accept this order.");
    } finally {
      setProcessing(false);
    }
  };

  const reject = async () => {
    if (processing) return;
    if (!window.confirm("Reject this order and release the authorization? The customer's card was never charged.")) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/tenant-review/reject`, { reason: rejectReason });
      if (data.status === "reconciliation_required") {
        setOutcome({ kind: "reconciliation_required", message: data.message });
        return;
      }
      if (data.status === "release_failed") {
        toast.error(data.message || "The authorization could not be released.");
        return;
      }
      toast.success("Order rejected — authorization released.");
      onResolved && onResolved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not reject this order.");
    } finally {
      setProcessing(false);
    }
  };

  // All-items-removed correction (Aug 2026, UBE-ORD-000030) — confirmed
  // gap: reducing every item to zero and clicking Accept previously hit
  // `accept_and_capture`'s own `cannot_remove_all_items` guard, surfacing
  // only a generic "Could not accept this order" error — never the
  // dedicated, accurate "this order will be rejected and the customer
  // was never charged" outcome the tenant actually intends. Reuses the
  // EXISTING `/tenant-review/reject` endpoint verbatim (never a new
  // capture/release code path) — an empty adjusted order is exactly a
  // full-authorization release, the same outcome the ordinary Reject
  // action already produces.
  const rejectEmptyOrder = async () => {
    if (processing || previewNotReady) return;
    if (!window.confirm(
      "Every item has been removed from this order. Reject it and release the full authorization? The customer will not be charged.",
    )) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/tenant-review/reject`, {
        reason: "All items removed during tenant review — order rejected, full authorization released.",
      });
      if (data.status === "reconciliation_required") {
        setOutcome({ kind: "reconciliation_required", message: data.message });
        return;
      }
      if (data.status === "release_failed") {
        toast.error(data.message || "The authorization could not be released.");
        return;
      }
      toast.success("Order rejected — full authorization released. The customer was not charged.");
      onResolved && onResolved();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not reject this order.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center" data-testid="retail-tenant-review-modal">
      <div className="absolute inset-0 bg-black/50" onClick={processing ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-base font-semibold">Review Order</h3>
          <button type="button" onClick={onClose} disabled={processing} data-testid="retail-tenant-review-close" className="text-slate-400 hover:text-slate-700 disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="py-10 grid place-items-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>
        ) : outcome?.kind === "reconciliation_required" ? (
          <div className="rounded-md bg-purple-50 border border-purple-200 text-purple-800 text-sm px-3 py-3" data-testid="retail-tenant-review-reconciliation-required">
            <div className="font-medium mb-1">Reconciliation Required</div>
            <div>{outcome.message}</div>
            <div className="mt-2 text-xs text-purple-700">Do not retry this action. Contact support to resolve this order.</div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="retail-tenant-review-items">
              {originalItems.map((it) => {
                const id = it.item_id || it.id;
                const qty = quantities[id] ?? it.quantity;
                const removed = qty === 0;
                return (
                  <li key={id} className={`px-3 py-2 flex items-center justify-between gap-3 ${removed ? "opacity-50" : ""}`} data-testid={`retail-tenant-review-item-${id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.product_name}</div>
                      <div className="text-xs text-slate-500">{formatMoney((it.unit_price || 0) * 100, currency)} each</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => decrement(id, it.quantity)} disabled={processing || qty === 0} data-testid={`retail-tenant-review-item-decrement-${id}`} className="w-6 h-6 rounded border border-slate-300 text-slate-600 disabled:opacity-40">−</button>
                      <span className="w-6 text-center" data-testid={`retail-tenant-review-item-qty-${id}`}>{qty}</span>
                      <button type="button" onClick={() => increment(id, it.quantity)} disabled={processing || qty >= it.quantity} data-testid={`retail-tenant-review-item-increment-${id}`} className="w-6 h-6 rounded border border-slate-300 text-slate-600 disabled:opacity-40">+</button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {previewStale && !previewFailed && (
              <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="retail-tenant-review-recalculating">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Recalculating totals…
              </div>
            )}
            {previewFailed && (
              <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 flex items-center justify-between gap-2" data-testid="retail-tenant-review-preview-error">
                <span>Could not refresh totals.</span>
                <button type="button" onClick={retryPreview} className="underline font-medium" data-testid="retail-tenant-review-preview-retry">Retry</button>
              </div>
            )}

            {isEmptyOrder ? (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-0.5" data-testid="retail-tenant-review-empty-order-notice">
                <div className="font-medium">Every item has been removed</div>
                <div className="flex justify-between"><span>Item subtotal</span><span>{formatMoney(0, currency)}</span></div>
                <div className="flex justify-between font-medium"><span>Proposed capture</span><span data-testid="retail-tenant-review-proposed-capture">{formatMoney(0, currency)}</span></div>
                <div className="flex justify-between"><span>Authorization to release</span><span data-testid="retail-tenant-review-to-release">{formatMoney(preview?.amount_to_be_released_cents, currency)}</span></div>
                <div className="pt-1">This order will be rejected — the customer will not be charged.</div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-y-1 text-xs text-slate-600">
                  <div>Subtotal</div><div className="text-right">{formatMoney((preview?.subtotal || 0) * 100, currency)}</div>
                  <div>Discount</div><div className="text-right">-{formatMoney((preview?.discount || 0) * 100, currency)}</div>
                  <div>Tax</div><div className="text-right">{formatMoney((preview?.tax || 0) * 100, currency)}</div>
                  <div>Delivery</div><div className="text-right">{formatMoney((preview?.delivery || 0) * 100, currency)}</div>
                  <div>Tip</div><div className="text-right">{formatMoney((preview?.tip || 0) * 100, currency)}</div>
                  <div className="font-medium text-slate-800">Order total</div>
                  <div className="text-right font-medium text-slate-800" data-testid="retail-tenant-review-total">{formatMoney((preview?.total || 0) * 100, currency)}</div>
                </div>

                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-0.5">
                  <div className="flex justify-between"><span>Authorized maximum</span><span data-testid="retail-tenant-review-authorized-max">{formatMoney(preview?.authorized_amount_cents, currency)}</span></div>
                  <div className="flex justify-between font-medium"><span>Proposed final capture</span><span data-testid="retail-tenant-review-proposed-capture">{formatMoney(preview?.proposed_final_capture_amount_cents, currency)}</span></div>
                  <div className="flex justify-between"><span>Amount to be released</span><span data-testid="retail-tenant-review-to-release">{formatMoney(preview?.amount_to_be_released_cents, currency)}</span></div>
                  {preview?.is_local_delivery && preview?.local_delivery_quote_expires_at && (
                    <div className="flex justify-between"><span>Delivery quote expires</span><span>{new Date(preview.local_delivery_quote_expires_at).toLocaleTimeString()}</span></div>
                  )}
                </div>
              </>
            )}

            {(preview?.allowed_fulfillment_choices || []).length > 1 && (
              <div className="rounded-md border border-slate-200 px-3 py-2 space-y-1.5" data-testid="retail-tenant-review-fulfillment-choice">
                <div className="text-xs font-medium text-slate-700">How will this order be delivered?</div>
                {preview.allowed_fulfillment_choices.map((choice) => (
                  <label key={choice} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="radio"
                      name="fulfillment_choice"
                      value={choice}
                      checked={fulfillmentChoice === choice}
                      onChange={() => setFulfillmentChoice(choice)}
                      disabled={processing}
                      data-testid={`retail-tenant-review-fulfillment-choice-${choice}`}
                    />
                    {choice === "uber" ? "Accept & Request Uber (as quoted)" : "Accept & Use Own Delivery"}
                  </label>
                ))}
              </div>
            )}

            {preview?.authorization_capture_deadline && (
              <div
                className={`rounded-md border px-3 py-2 text-xs ${
                  new Date(preview.authorization_capture_deadline).getTime() - Date.now() < 24 * 60 * 60 * 1000
                    ? "bg-red-50 border-red-200 text-red-800"
                    : "bg-slate-50 border-slate-200 text-slate-600"
                }`}
                data-testid="retail-tenant-review-authorization-deadline"
              >
                Card authorization must be captured by{" "}
                <strong>{new Date(preview.authorization_capture_deadline).toLocaleString()}</strong>, or the
                customer's bank will automatically release it.
              </div>
            )}

            {isEmptyOrder ? (
              <button
                type="button"
                onClick={rejectEmptyOrder}
                disabled={processing || previewNotReady}
                data-testid="retail-tenant-review-reject-empty"
                className="w-full px-3 py-2 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-60"
              >
                {processing ? "Releasing…" : "Reject order and release full authorization"}
              </button>
            ) : showRejectReason ? (
              <div className="space-y-2">
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejecting this order (visible to your own team only)"
                  data-testid="retail-tenant-review-reject-reason"
                  className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
                  rows={2}
                  disabled={processing}
                />
                <div className="flex gap-2">
                  <button type="button" onClick={reject} disabled={processing} data-testid="retail-tenant-review-reject-confirm" className="flex-1 px-3 py-2 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-60">
                    {processing ? "Releasing…" : "Confirm Reject & Release"}
                  </button>
                  <button type="button" onClick={() => setShowRejectReason(false)} disabled={processing} className="px-3 py-2 rounded-md border border-slate-300 text-xs">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <button type="button" onClick={accept} disabled={processing || previewNotReady} data-testid="retail-tenant-review-accept" className="flex-1 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-60">
                  {processing
                    ? "Processing…"
                    : isPickupOrder
                    ? (hasAdjustments ? "Accept Adjusted Order & Customer Pickup" : "Accept & Customer Pickup")
                    : isUberChoice
                    ? (hasAdjustments ? "Accept Adjusted Order & Request Uber" : "Accept & Request Uber")
                    : isOwnDeliveryChoice
                    ? (hasAdjustments ? "Accept Adjusted Order & Use Own Delivery" : "Accept & Use Own Delivery")
                    : hasAdjustments
                    ? "Accept Adjusted Order & Capture"
                    : "Accept & Capture"}
                </button>
                <button type="button" onClick={() => setShowRejectReason(true)} disabled={processing} data-testid="retail-tenant-review-reject" className="px-3 py-2 rounded-md border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50 disabled:opacity-60">
                  Reject & Release
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
